// Competition/Match Types for Website

export enum Organizer {
  GOODFELLAS = 'goodfellas',
  IBJJF = 'ibjjf',
  CBJJ = 'cbjj',
  SJJSAF = 'sjjsaf',
  AJP = 'ajp',
  OTHER = 'other',
}

/**
 * Modalidade do desafio Goodfellas. Só existe quando `organizer` é
 * `goodfellas`; ausência significa modalidade desconhecida (luta legada).
 */
export enum ChallengeType {
  ONE_ON_ONE = 'one_on_one',
  FOUR_ON_FOUR = 'four_on_four',
}

export enum MatchResult {
  WON = 'won',
  LOST = 'lost',
  DRAW = 'draw',
}

export enum Decision {
  SUBMISSION = 'submission',
  POINTS = 'points',
  ADVANTAGES = 'advantages',
  DISQUALIFICATION = 'disqualification',
  REFEREE = 'referee',
  WALKOVER = 'walkover',
  INJURY = 'injury',
  WITHDRAWAL = 'withdrawal',
  CROWD_DECISION = 'crowd_decision',
}

export enum SubmissionType {
  ARMBAR = 'armbar',
  TRIANGLE = 'triangle',
  KIMURA = 'kimura',
  AMERICANA = 'americana',
  OMOPLATA = 'omoplata',
  REAR_NAKED_CHOKE = 'rear_naked_choke',
  GUILLOTINE = 'guillotine',
  BOW_AND_ARROW = 'bow_and_arrow',
  EZEKIEL = 'ezekiel',
  LOOP_CHOKE = 'loop_choke',
  CROSS_CHOKE = 'cross_choke',
  CLOCK_CHOKE = 'clock_choke',
  BASEBALL_CHOKE = 'baseball_choke',
  DARCE = 'darce',
  ANACONDA = 'anaconda',
  NORTH_SOUTH_CHOKE = 'north_south_choke',
  HEEL_HOOK = 'heel_hook',
  KNEE_BAR = 'knee_bar',
  TOE_HOLD = 'toe_hold',
  ANKLE_LOCK = 'ankle_lock',
  CALF_SLICER = 'calf_slicer',
  WRIST_LOCK = 'wrist_lock',
  CHOKE = 'choke',
  BICEP_SLICER = 'bicep_slicer',
  OTHER = 'other',
}

export interface Identificator {
  type: string
  uuid: string
}

export interface Belt {
  color: string
  degree: number
}

export interface Category {
  weight?: string
  age?: string
  belt?: Belt
}

/**
 * Mestre de um competidor: sempre um perfil real, escolhido entre os
 * instrutores da academia dele. Espelha `masterSchema` de
 * `schemas/competition/src/match.ts`.
 */
export interface MatchMaster {
  name: string
  identificator: Identificator
}

export interface Opponent {
  name: string
  academy?: string
  identificator?: Identificator | null
  master?: MatchMaster | null
}

export interface MatchStatistics {
  points?: number
  advantages?: number
  penalties?: number
  timeSeconds?: number
  opponentPoints?: number
  opponentAdvantages?: number
  opponentPenalties?: number
}

/**
 * Estado da confirmação do resultado pela outra parte (#1292). Espelha o
 * `confirmationSchema` de `schemas/competition/src/match.ts`. A ausência do
 * campo significa luta legada, tratada como confirmada.
 */
export enum ConfirmationStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  DISPUTED = 'disputed',
  ARBITRATION = 'arbitration',
}

export interface MatchDispute {
  /** A versão do oponente sobre quem venceu. */
  result: MatchResult
  reason?: string | null
  reportedAt?: string | Date
}

export interface MatchConfirmation {
  status: ConfirmationStatus
  source?: 'opponent' | 'admin' | 'system'
  respondedAt?: string | Date
  respondedByUuid?: string
  notifiedAt?: string | Date
  escalatedAt?: string | Date
  dispute?: MatchDispute | null
}

export interface CompetitionMatch {
  identificator: Identificator
  eventName: string
  eventDate: string | Date
  organizer: Organizer
  challengeType?: ChallengeType
  category?: Category
  opponent: Opponent
  result: MatchResult
  decision: Decision
  submission: boolean
  submissionType?: SubmissionType | null
  videos: string[]
  photos: string[]
  statistics?: MatchStatistics
  fighter: Identificator
  fighterMaster?: MatchMaster | null
  confirmation?: MatchConfirmation
  creation: string | Date
  updated: string | Date
  revision: number
}

// Form input types for creating/editing matches
export interface MatchFormInput {
  eventName: string
  eventDate: string
  organizer: Organizer
  challengeType?: ChallengeType
  category?: {
    weight?: string
    age?: string
    beltColor?: string
    beltDegree?: number
  }
  opponentName: string
  opponentAcademy?: string
  opponentIdentificator?: string // UUID if exists
  fighterMaster?: MatchMaster | null
  opponentMaster?: MatchMaster | null
  result: MatchResult
  decision: Decision
  submission: boolean
  submissionType?: SubmissionType
  videos?: string[]
  photos?: string[]
  points?: number
  advantages?: number
  penalties?: number
  timeSeconds?: number
  opponentPoints?: number
  opponentAdvantages?: number
  opponentPenalties?: number
}

// Competition history summary
export interface CompetitionStats {
  totalMatches: number
  wins: number
  losses: number
  draws: number
  submissionWins: number
  submissionLosses: number
  winRate: number
}

// Helper labels for display
export const OrganizerLabels: Record<Organizer, string> = {
  [Organizer.GOODFELLAS]: 'Goodfellas',
  [Organizer.IBJJF]: 'IBJJF',
  [Organizer.CBJJ]: 'CBJJ',
  [Organizer.SJJSAF]: 'SJJSAF',
  [Organizer.AJP]: 'AJP',
  [Organizer.OTHER]: 'Outro',
}

export const ChallengeTypeLabels: Record<ChallengeType, string> = {
  [ChallengeType.ONE_ON_ONE]: 'Desafio 1x1',
  [ChallengeType.FOUR_ON_FOUR]: 'Desafio 4x4',
}

export const ResultLabels: Record<MatchResult, string> = {
  [MatchResult.WON]: 'Vitória',
  [MatchResult.LOST]: 'Derrota',
  [MatchResult.DRAW]: 'Empate',
}

export const ResultLabelsShort: Record<MatchResult, string> = {
  [MatchResult.WON]: 'V',
  [MatchResult.LOST]: 'D',
  [MatchResult.DRAW]: 'E',
}

export const DecisionLabels: Record<Decision, string> = {
  [Decision.SUBMISSION]: 'Finalização',
  [Decision.POINTS]: 'Pontos',
  [Decision.ADVANTAGES]: 'Vantagens',
  [Decision.DISQUALIFICATION]: 'Desqualificação',
  [Decision.REFEREE]: 'Decisão do Árbitro',
  [Decision.WALKOVER]: 'WO',
  [Decision.INJURY]: 'Lesão',
  [Decision.WITHDRAWAL]: 'Desistência',
  [Decision.CROWD_DECISION]: 'Decisão do Público',
}

export const SubmissionTypeLabels: Record<SubmissionType, string> = {
  [SubmissionType.ARMBAR]: 'Armlock',
  [SubmissionType.TRIANGLE]: 'Triângulo',
  [SubmissionType.KIMURA]: 'Kimura',
  [SubmissionType.AMERICANA]: 'Americana',
  [SubmissionType.OMOPLATA]: 'Omoplata',
  [SubmissionType.REAR_NAKED_CHOKE]: 'Mata Leão',
  [SubmissionType.GUILLOTINE]: 'Guilhotina',
  [SubmissionType.BOW_AND_ARROW]: 'Arco e Flecha',
  [SubmissionType.EZEKIEL]: 'Ezequiel',
  [SubmissionType.LOOP_CHOKE]: 'Loop Choke',
  [SubmissionType.CROSS_CHOKE]: 'Estrang. Cruzado',
  [SubmissionType.CLOCK_CHOKE]: 'Clock Choke',
  [SubmissionType.BASEBALL_CHOKE]: 'Baseball Choke',
  [SubmissionType.DARCE]: "D'arce",
  [SubmissionType.ANACONDA]: 'Anaconda',
  [SubmissionType.NORTH_SOUTH_CHOKE]: 'North South',
  [SubmissionType.HEEL_HOOK]: 'Heel Hook',
  [SubmissionType.KNEE_BAR]: 'Knee Bar',
  [SubmissionType.TOE_HOLD]: 'Toe Hold',
  [SubmissionType.ANKLE_LOCK]: 'Botinha',
  [SubmissionType.CALF_SLICER]: 'Calf Slicer',
  [SubmissionType.WRIST_LOCK]: 'Wrist Lock',
  [SubmissionType.CHOKE]: 'Estrangulamento',
  [SubmissionType.BICEP_SLICER]: 'Mão de Vaca',
  [SubmissionType.OTHER]: 'Outro',
}

// Weight categories
export const WeightCategories = [
  'Galo (57kg)',
  'Pluma (64kg)',
  'Pena (70kg)',
  'Leve (76kg)',
  'Médio (82kg)',
  'Meio-Pesado (88kg)',
  'Pesado (94kg)',
  'Super-Pesado (100kg)',
  'Pesadíssimo (+100kg)',
  'Absoluto',
]

// Age categories
export const AgeCategories = [
  'Juvenil (16-17)',
  'Adulto (18-29)',
  'Master 1 (30-35)',
  'Master 2 (36-40)',
  'Master 3 (41-45)',
  'Master 4 (46-50)',
  'Master 5 (51-55)',
  'Master 6 (56+)',
]

// Helper function to calculate competition stats
export function calculateCompetitionStats(
  matches: CompetitionMatch[],
): CompetitionStats {
  const totalMatches = matches.length
  const wins = matches.filter((m) => m.result === MatchResult.WON).length
  const losses = matches.filter((m) => m.result === MatchResult.LOST).length
  const draws = matches.filter((m) => m.result === MatchResult.DRAW).length
  const submissionWins = matches.filter(
    (m) => m.result === MatchResult.WON && m.decision === Decision.SUBMISSION,
  ).length
  const submissionLosses = matches.filter(
    (m) => m.result === MatchResult.LOST && m.decision === Decision.SUBMISSION,
  ).length

  return {
    totalMatches,
    wins,
    losses,
    draws,
    submissionWins,
    submissionLosses,
    winRate: totalMatches > 0 ? (wins / totalMatches) * 100 : 0,
  }
}

// Helper function to format decision for display
export function formatDecision(match: CompetitionMatch): string {
  if (match.decision === Decision.SUBMISSION && match.submissionType) {
    return `${DecisionLabels[match.decision]} (${
      SubmissionTypeLabels[match.submissionType]
    })`
  }
  return DecisionLabels[match.decision]
}
